import { useTranslation } from 'react-i18next';
import {
  type AssigneeModel,
  type EntityOrSuggestionRef,
  type PopulatedUser,
  type PopulatedOrgFunction,
  type PopulatedSystem,
  type PopulatedExternalContact
} from '@bliks-io/contracts';
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
  UserCard,
  SystemCard,
  OrgFunctionCard,
  ExternalContactCard,
  getUserInitials,
  toUserCardProps
} from '@bliks-io/ui';
import { useResolvedAssignee } from '@/lib/useAssigneeLookup';

interface StepCardUserHeaderProps {
  recordedBy?: PopulatedUser | null;
  /** Unpopulated `{ type, id }` assignee ref — resolved against org lists here. */
  assignedTo?: EntityOrSuggestionRef | null;
  assignedToModel?: AssigneeModel;
}

/**
 * User information header for the step card node
 * Displays the entity this step was recorded for (User, OrgFunction, System, or ExternalContact)
 *
 * `assignedTo` arrives as an `EntityOrSuggestionRef`; it is resolved against the
 * org's user/orgFunction/system/contact lists before rendering the matching card.
 * Unresolved refs (suggestion, or missing entity) fall back to `recordedBy`.
 * Soft-deleted assignees still resolve (the lookup opts into deleted entities)
 * and render grayed out with a "(deleted)" suffix instead of falling back —
 * the cards self-decorate off the resolved entity's `deletedAt`.
 */
export function StepCardUserHeader({
  recordedBy,
  assignedTo,
  assignedToModel
}: StepCardUserHeaderProps) {
  const { t } = useTranslation();

  const resolved = useResolvedAssignee(assignedTo, assignedToModel);

  // Render OrgFunction header
  if (assignedToModel === 'OrgFunction' && resolved) {
    return (
      <div className="mb-4">
        <OrgFunctionCard
          orgFunction={resolved as PopulatedOrgFunction}
          variant="with-email"
          itemVariant="default"
          size="default"
          className="p-0"
        />
      </div>
    );
  }

  // Render System header
  if (assignedToModel === 'System' && resolved) {
    return (
      <div className="mb-4">
        <SystemCard
          system={resolved as PopulatedSystem}
          variant="with-category"
          itemVariant="default"
          size="default"
          className="p-0"
        />
      </div>
    );
  }

  // Render ExternalContact header
  if (assignedToModel === 'ExternalContact' && resolved) {
    return (
      <div className="mb-4">
        <ExternalContactCard
          contact={resolved as PopulatedExternalContact}
          variant="with-type"
          itemVariant="default"
          size="default"
          className="p-0"
        />
      </div>
    );
  }

  // Default: User header. Prefer the resolved assignee (User model); fall back
  // to recordedBy. This branch also catches a Team/System/ExternalContact
  // assignee that didn't resolve (missing from the lookup) — showing who
  // actually recorded the step is a better default than a blank header.
  // `deletedAt` only survives on the lookup-resolved user (the backend strips
  // it from populated users like recordedBy) — that's exactly the case where
  // the deleted treatment applies.
  const user =
    (assignedToModel === 'User' ? (resolved as PopulatedUser | undefined) : undefined) ??
    recordedBy;
  if (user && user.firstName && user.lastName) {
    return (
      <div className="mb-4">
        <UserCard
          user={toUserCardProps(user)}
          variant="with-role"
          itemVariant="default"
          size="default"
          className="p-0"
        />
      </div>
    );
  }

  // Fallback for unknown users
  const unknownUserName = t('viralRecording.labels.unknownUser');
  const unknownRole = t('viralRecording.labels.unknownRole');

  return (
    <div className="flex items-center gap-3 mb-4">
      <Avatar className="h-10 w-10 rounded-lg border-[1.5px] border-border-10">
        <AvatarImage src={undefined} alt={unknownUserName} />
        <AvatarFallback className="rounded-lg bg-yellow-avatar text-white">
          {getUserInitials(unknownUserName, '')}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1">
        <div className="text-base font-semibold">
          {t('viralRecording.labels.youLabel', { name: unknownUserName })}
        </div>
        <div className="text-sm text-muted-foreground">{unknownRole}</div>
      </div>
    </div>
  );
}
